/**
 * The two consumers of the in-flight upload registry.
 *
 * `useUploadUnloadGuard` — warns before a reload / tab close that would
 * kill an upload. The S3 PUT is a `fetch` owned by the page, so unloading
 * aborts it; the presigned row survives on the server, which is how a
 * refresh mid-upload used to leave a file that could never be opened.
 *
 * `useUploadingNodeIds` — lets the tree ask whether THIS tab is the one
 * uploading a given node, so a genuinely-busy row and an abandoned ghost
 * can render differently.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'

import type { PutProgress } from '@/features/uploads/put-with-progress'
import {
  getProgressSnapshot,
  getUploadSnapshot,
  inFlightUploadCount,
  subscribeToUploads,
} from '@/features/uploads/upload-registry'

/**
 * Install a `beforeunload` guard for as long as this tab owns an upload.
 *
 * Browsers deliberately ignore custom text here and show their own
 * "Leave site?" dialog — `preventDefault()` plus a legacy `returnValue`
 * is the whole API, so there is nothing to localize. It also only fires
 * for a reload / close / navigation-away; it cannot catch a crash, a
 * kill, or a machine sleeping, which is exactly why it is a nudge and not
 * the fix. The fix is that an abandoned row says so and the reaper
 * retires it.
 */
export function useUploadUnloadGuard(): void {
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (inFlightUploadCount() === 0) return
      event.preventDefault()
      // Legacy channel some browsers still require to show the prompt.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])
}

/** The node ids this tab is currently uploading. Re-renders on change. */
export function useUploadingNodeIds(): readonly string[] {
  return useSyncExternalStore(subscribeToUploads, getUploadSnapshot, getUploadSnapshot)
}

/**
 * Byte progress for ONE upload, or null when it is not in flight (or has
 * not started moving bytes yet — render that as indeterminate, not 0%).
 *
 * Subscribes to the progress snapshot rather than the id snapshot, so a
 * component watching one row is not re-rendered by every other upload's
 * membership change. The store contract needs a stable reference between
 * notifications, which is why this reads out of the cached snapshot map
 * instead of building a fresh object per call.
 */
export function useUploadProgress(key: string | null | undefined): PutProgress | null {
  // Selector-based, NOT "subscribe to the whole map and index into it".
  //
  // `getProgressSnapshot` returns a fresh Map on every tick, so reading it
  // wholesale makes `useSyncExternalStore` re-render EVERY subscriber on
  // every percent — and the wiki tree calls this once per row, so a single
  // upload would re-render hundreds of idle rows about a hundred times.
  // Returning the per-key value means the store compares the progress
  // object itself, and a row whose key is absent sees `null` every time
  // and never re-renders.
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeToUploads(onStoreChange),
    [],
  )
  const getSnapshot = useCallback(
    () => (key ? (getProgressSnapshot().get(key) ?? null) : null),
    [key],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
